import { memo } from 'react';

import {
  roundAverageRating,
  STAR_RATING_MIN_DISPLAY,
} from '@virginexperiencedays/components-v2/src/utils/productRating';

import { Button } from '@virginexperiencedays/components-v2/src/layout/Button';
import { DisplayHeading } from '@virginexperiencedays/components-v2/src/typography/DisplayHeading';
import { cn } from '@virginexperiencedays/tailwind';

import { MediaGrid } from '../MediaGrid';

import { DataTestId } from '../VariationPage/PageUI';
import { ReviewSummary } from '../Reviews/Reviews';
import {
  additionalDetailsExtractor,
  mediaGridExtractor,
  overviewExtractor,
} from '../VariationPage/extractors';
import { AdditionalDetailsWidget } from '../Widgets/AdditionalDetailsWidget';
import { OverviewWidget } from '../Widgets/OverviewWidget';
import {
  trackPdpCollectionChoice,
  PdpInteraction,
  PdpModules,
  type PdpInteractionProps,
} from '../../../../libs/tracking/pdp';

import type { CollectionChoiceOverlayProps } from './index';
import { CollectionChoiceOverlayContentSkeleton } from './CollectionChoiceOverlayContentSkeleton';

export type CollectionChoiceOverlayContentProps = Pick<
  CollectionChoiceOverlayProps,
  'product' | 'isLoadingCollectionChoiceInfo'
> & {
  onClose: () => void;
  trackingData?: PdpInteractionProps;
};

export const CollectionChoiceOverlayContent = memo(function CollectionChoiceOverlayContent({
  onClose,
  product,
  trackingData,
  isLoadingCollectionChoiceInfo,
}: CollectionChoiceOverlayContentProps) {
  const additionalDetails = additionalDetailsExtractor(product, true);
  const overview = overviewExtractor(product);
  const mediaGrid = mediaGridExtractor(product?.media);
  const roundedRating =
    product?.reviews?.averageRating >= STAR_RATING_MIN_DISPLAY
      ? roundAverageRating(product?.reviews?.averageRating)
      : null;

  const wrapperStyling = cn([
    'flex flex-col gap-6',
    'overflow-y-auto overflow-x-hidden hide-scrollbar',
    // drawer
    // 90dvh less heights of drawer handle, section heading,
    // the gap between product title and this, and drawer body padding bottom respectively
    'max-h-[calc(90dvh_-_(20px_+_22px_+_16px_+_16px))] pb-10',
    // modal
    'md:max-h-none md:overflow-y-visible md:pb-0',
  ]);

  if (!product || isLoadingCollectionChoiceInfo) {
    return <CollectionChoiceOverlayContentSkeleton className={wrapperStyling} />;
  }

  return (
    <div className={wrapperStyling}>
      <section className="-mb-2 flex flex-col gap-2">
        <DisplayHeading
          data-testid={DataTestId.Title}
          className="text-lg font-semibold leading-tight lg:text-[22px] lg:font-bold"
        >
          {product.title}
        </DisplayHeading>
        {!!roundedRating && (
          <ReviewSummary
            averageRating={product.reviews?.averageRating}
            averageRatingDisplay={roundedRating.toString()}
          />
        )}
      </section>

      <MediaGrid className="shrink-0" data-vaul-no-drag {...mediaGrid} />

      <OverviewWidget
        hasUspGrid={false}
        {...overview}
        onAboutExpandCollapse={({ isExpanded, moduleTitle }) => {
          const interaction = isExpanded
            ? PdpInteraction.ClickReadLess
            : PdpInteraction.ClickReadMore;

          trackPdpCollectionChoice({
            ...trackingData,
            interaction,
            module: PdpModules.About,
            moduleTitle,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
      />

      <AdditionalDetailsWidget
        additionalDetails={additionalDetails}
        locations={product.locations}
        sku={product.sku}
        hasLocationsDropdown
        onAccordionToggle={({ isExpanded, text }) => {
          const interaction = isExpanded
            ? PdpInteraction.ClickAccordionCollapse
            : PdpInteraction.ClickAccordionExpand;

          trackPdpCollectionChoice({
            ...trackingData,
            interaction,
            module: PdpModules.AdditionalDetails,
            moduleTitle: text,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
        onExpandCollapse={({ isExpanded, moduleTitle }) => {
          const interaction = isExpanded
            ? PdpInteraction.ClickReadLess
            : PdpInteraction.ClickReadMore;

          trackPdpCollectionChoice({
            ...trackingData,
            interaction,
            module: PdpModules.AdditionalDetails,
            moduleTitle,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
        onClickMap={({ text }) => {
          trackPdpCollectionChoice({
            ...trackingData,
            interaction: PdpInteraction.ClickMap,
            module: PdpModules.Locations,
            moduleTitle: text,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
        onClickLocationSelect={({ text }) => {
          trackPdpCollectionChoice({
            ...trackingData,
            interaction: PdpInteraction.ClickLocationSelect,
            module: PdpModules.Locations,
            moduleTitle: text,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
        onSelectLocation={({ text }) => {
          trackPdpCollectionChoice({
            ...trackingData,
            interaction: PdpInteraction.SelectLocation,
            module: PdpModules.Locations,
            moduleTitle: text,
            collectionChoiceSlug: product.slug,
            collectionChoiceTitle: product.title,
          });
        }}
      />
      <div className="pb-[env(safe-area-inset-bottom)]">
        <Button
          className="w-full py-[18px] text-lg leading-none"
          variant="outline"
          onClick={onClose}
        >
          Back to collection
        </Button>
      </div>
    </div>
  );
});
